'use client';
import dynamic from 'next/dynamic';
import { Box } from '@mui/material';
import React, {
    createRef,
    ReactNode,
    RefObject,
    useEffect,
    useRef,
    useState,
} from 'react';
import styles from './styles.module.scss';

import { useAnalytics } from '@/integrations';
import { useTranslation, useWindowDimensions } from '@/hook';
import {
    get3dImperfections,
    setImperfectionsImages,
    setImperfectionsImagesCategories,
} from '@/redux/reducers/gallery.reducer';
import { useAppSelector } from '@/hook/redux-hooks';
import {
    StyledTab,
    StyledTabs,
} from '@/components/shared/TabsThemed/tabs-themed.component';
import ValuePropositionSlider from '@/components/shared/value-proposition-slider/value-proposition-slider.component';
import CarDisplayCardClient from '@/components/shared/car-details-card/car-details-car-client.component';
import Modal from '@/components/shared/modal.component';
import Typography from '@/components/shared/typography/typography.component';
import SylndrButton from '@/components/shared/Button/button-styled.component';
import {
    banners,
    bannersAR,
    carFrame,
    ComfortConvenienceIconImg,
    DimensionsCapacityIconImg,
    engine,
    EntertainmentCommunicationIconImg,
    ExteriorIconImg,
    FaultyCheckMark,
    FixedMark,
    HighlightsIcon,
    imgs,
    ImperfectionCheckMark,
    InteriorIconImg,
    PerformanceFeatureIconImg,
    SafetyIconImg,
    SylndrCertified,
    SylndrCertifiedIcon,
} from '../../../public/Images';
import Image from 'next/image';
import {
    AppDispatch,
    CarDetailsType,
    CarFeatureInterface,
    DisplayedCarInfoInterface,
    Feature_SKELETON,
    GetCarListing,
    ImperfectionReportItem,
    ImperfectionReportSection,
    ImperfectionReportSectionGrouped,
    ImperfectionStatusEnum,
    IQCReportUIProperty,
    isReserveHomeAllowed,
    isReserveHubAllowed,
    isSeeHomeAllowed,
    isSeeHubAllowed,
} from '@/types';
import { useDispatch } from 'react-redux';
import { ModalTabsText } from './CarProfileNew/StyledComponents/modal-components';
import {
    CarInfo,
    GridSection,
    ModalInfo,
    NotifyMeButton,
} from './CarProfileNew';
import { EGGRID_ITEM_VARIANT } from './CarProfileNew/GridSection/car-grid.component';
import { formatDate, getCarCardLocale, numberWithCommas } from '@/utils';
import { CarProfileContext } from '@/contexts/carProfile.context';
import Link from '@/components/WrappedLink';
import CarSoldInfoBoxMobile from './CarProfileNew/SimilarCars/CarSoldInfoBoxMobile';
import { CarListingData } from '@/types/cars.types';
import {
    AVAILABLE_LANGUAGES,
    NEXT_APP_MAINTENANCE_DISABLE_BOOKING,
    ServiceName,
} from '@/constants';
import { IModalData } from '@/components/car-details/CarProfileNew/InfoModal/modal-info.component';
import FinanceCalculator from '@/components/shared/FinanceCalculator';
import myImageLoader from '@/image.loader';
import { DimensionsTargets } from '@/hook/use-window-width.hook';
import BlinkingSkeleton from '../shared/blinking-skeleton/blinking-skeleton.component';

import ReserveInfoBox from './ReserveInfoBox/reserve-info-box.component';
import { toggleLoading } from '@/redux/reducers/checkoutReducer';
import VidTestimonialsSection from '@/components/testimonialVidSection/vid-testimonials.component';
import { SourceScreen } from '@/types/source-screens.types';
import { TFebDiscounts } from '@/api-requests/feb-discounts.apis';
import Carousel from '@/components/shared/carousel/carousel.component';
import CarouselV2 from '@/components/shared/carouselv2/carouselv2.component';
import { PurchaseCarSteps } from '@/components/purchaseCarSteps';
import GrouppedImprefectionReportModal from './CarProfileNew/ImprefectionReportGroupped/imprefection-report-modal.component';
import GrouppedImprefectionReportBottomSheet from './CarProfileNew/ImprefectionReportGroupped/imprefection-report-bottomsheet.component';
import { CarGuarantee } from './CarProfileNew/CarInfoSection/car-info.component';

const SimilarCarsContainer = dynamic(
    () =>
        import('./CarProfileNew/SimilarCars/SimilarCarsContainer').then(
            mod => mod.default
        ),
    {
        ssr: false,
    }
);

const ImperfectionOverviewSectioned = dynamic(
    () =>
        import(
            './CarProfileNew/ImperfectionSection/imperfection-overview-sectioned.component'
        ).then(mod => mod.default),
    { ssr: false }
);

const Gallery = dynamic(
    () => import('./CarProfileNew/Gallery').then(mod => mod.Gallery),
    {
        ssr: false,
        loading: () => <ProfileSkeleton />,
    }
);
const SwipeableDrawer = dynamic(
    () =>
        import('@/components/shared/bottom-sheet/bottom-sheet.component').then(
            mod => mod.default
        ),
    {
        ssr: false,
    }
);

const ProfileSkeleton = () => {
    const { IS_DESKTOP } = useWindowDimensions(DimensionsTargets.MOBILE);
    const newGalleryHeight =
        'calc( 68.6vw + 20vw + 16px + 16px + 40px + 40px + 8px + 8px)';
    return (
        <BlinkingSkeleton
            height={IS_DESKTOP ? '50vh' : newGalleryHeight}
            minHeight={IS_DESKTOP ? '45vw' : '50vh'}
        />
    );
};

export default function CarDetailsComponent({
    lang,
    carDetails,
    imperfectionDataGroupped,
    similarCars,
    febDiscounts,
}: {
    lang: AVAILABLE_LANGUAGES;
    carDetails: CarDetailsType;
    similarCars: GetCarListing;
    febDiscounts: TFebDiscounts;
    imperfectionDataGroupped: ImperfectionReportSectionGrouped;
}) {
    const { t, IS_RTL } = useTranslation();
    const [value, setValue] = useState(0);

    const dispatch = useDispatch<AppDispatch>();

    const { IS_DESKTOP } = useWindowDimensions(DimensionsTargets.MOBILE);
    const [framesWidth, setFramesWidth] = useState(500);
    const [framesHeight, setFramesHeight] = useState(400);
    useEffect(() => {
        if (IS_DESKTOP && framesWidth !== 800) {
            setFramesWidth(800);
            setFramesHeight(800);
        } else if (!IS_DESKTOP && framesWidth !== 500) {
            setFramesWidth(500);
            setFramesHeight(400);
        }
    }, [IS_DESKTOP]);
    useEffect(() => {
        if (!carDetails) {
            return;
        }
        trackEvent('Screen: Car Profile', {
            carName: carDetails.sfVehicleName,
            make: carDetails.make,
            model: carDetails.model,
            year: carDetails.year,
            trim: carDetails.trim?.en,
            kilometers: carDetails.kilometers,
            price: carDetails.sylndrSellingPrice,
            status: carDetails.isPurchaseInProgress
                ? 'Being Sold'
                : carDetails.isReserved
                ? 'Reserved'
                : 'Not Reserved',
            monthlyInstallment: carDetails.installmentPrice,
            transmission: carDetails.transmission,
            //@TODO :: neeeds to be added. check the history of the file.
            // rTyres: rTyres?.faultyPoints ?? 0,
            // rExterior: rExterior?.faultyPoints ?? 0,
            // rInterior: rInterior?.faultyPoints ?? 0,
            // rMechanical: rMechanical?.faultyPoints ?? 0,
            // rElectricals: rElectricals?.faultyPoints ?? 0,
            // rSuspension: rSuspension?.faultyPoints ?? 0,
            newlyAdded:
                carDetails.isNewlyAdded &&
                !(carDetails.isReserved || carDetails.isPurchaseInProgress),
        });
    }, [carDetails]);

    const pageContainer = {
        paddingInline: IS_DESKTOP ? '5%' : '16px',
        paddingBlock: '3vw',
    };

    const gallery = {
        width: '100%',
        marginBottom: '2vw',
    };
    const carOverview = {
        width: '100%',
        marginBottom: '2vw',
    };
    const sectionHeader = {
        color: '#002D5D',
        opacity: 1,
        marginBottom: '8px',
    };

    const carFeature = {
        width: '100%',
        marginBottom: '2vw',
    };

    const carInspection = {
        width: '100%',
        marginBottom: '2vw',
    };

    const whySylndrContainer = {
        marginBottom: '2vw',
    };
    ////////////////////////////////////////////////
    const stickyContainer = {
        position: IS_DESKTOP ? 'sticky' : '',
        top: '20px',
        width: '100%',
        height: 'fit-content',
    };

    const similarCarsContainer = {
        marginTop: '32px',
        position: 'relative',
        borderTop: 'solid 1px #EAECF0',
    };
    const similarCarsText = {
        color: '#101828',
        position: 'absolute',
        top: 0,
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: 1,
        backgroundColor: 'white',
        padding: '0px 8px',
    };
    const buildCarOverview = (carDetails: any) => {
        return {
            make: t(carDetails?.make),
            model: carDetails?.model,
            year: carDetails?.year,
            Trim: IS_RTL ? carDetails?.trim?.ar : carDetails?.trim?.en,
            Color: t(carDetails?.color),
            kilometers: numberWithCommas(carDetails?.kilometers),
            Transmission:
                carDetails?.transmission === 'automatic'
                    ? t('automatic')
                    : t('manual'),
            'Body Style': t(carDetails?.bodyStyle),
            'Engine Capacity CC': carDetails?.engineCapacity,
            liscenseValidityDate: formatDate(carDetails?.liscenseValidityDate),
        };
    };
    const buildCarInfo = (carDetails: any): DisplayedCarInfoInterface => {
        return {
            id: carDetails?.id,
            make: t(carDetails?.make),
            model: carDetails?.model,
            year: carDetails?.year,
            trim: carDetails?.trim,
            kilometers: carDetails?.kilometers,
            transmission:
                carDetails?.transmission === 'automatic'
                    ? t('automatic')
                    : t('manual'),
            sylndrSellingPrice: carDetails?.sylndrSellingPrice,
            discountPrice: carDetails?.discountPrice,
            installmentPrice: carDetails?.installmentPrice,
            isReserved: carDetails?.isReserved,
            carName: carDetails?.sfVehicleName,
            isSold: carDetails?.isSold,
            isPublished: carDetails?.isPublished,
            isPurchaseInProgress: carDetails?.isPurchaseInProgress,
            showroom: carDetails?.showroom,
            registrationFees: carDetails?.registrationFees,
            bookingType: carDetails?.bookingType,
            isNewlyAdded: carDetails?.isNewlyAdded,
        };
    };

    const [openFeatureContainer, setOpenFeatureContainer] = useState(false);
    const [featureValue, setFeatureValue] = useState(0);
    const [carPrice, setCarPrice] = useState<number>();
    const [carDetailsState, setCarDetailsState] = useState<any>();
    const [initialOpenedTab, setInitialOpenedTab] = useState(0);

    const [userInterActed, setUserInterActed] = useState(false);
    const [scrollToPosition, setScrollToPosition] = useState<number>(0);
    const { trackEvent } = useAnalytics();
    const [carOverviewState = null, setCarOverviewState] = useState<any>();
    const [carInfo = null, setCarInfo] = useState<DisplayedCarInfoInterface>();
    const [carFeaturesNew, setCarFeaturesNew] = useState<IModalData[]>();

    const fireEvent = () => {
        if (!userInterActed) {
            setUserInterActed(true);
            trackEvent('Action: Gallery', {
                interaction: true,
                carName: carDetails?.sfVehicleName,
            });
        }
    };
    const [opacity, setOpacity] = useState<number>(1);
    const [openGrouppedImprefectionModal, setOpenGrouppedImprefectionModal] =
        useState(false);
    const closeGrouppedImprefectionModalHandler = () => {
        setOpenGrouppedImprefectionModal(false);
    };

    const listenScrollEvent = () => {
        if (!similarCarsStaticRef) return;
        const y = similarCarsStaticRef?.current?.offsetTop || 0;
        const diff: number = y - 700 - window.scrollY;
        const shouldBeginTransition: boolean = diff < 100 && diff >= 0;
        const shouldEndTransition: boolean = diff <= 0;
        setScrollToPosition(similarCarsStaticRef?.current?.offsetTop || 0);
        if (shouldEndTransition) {
            return setOpacity(0);
        } else if (shouldBeginTransition) {
            setOpacity(diff / 100);
        } else {
            setOpacity(1);
        }
    };
    //////////////////////////////////////////////////////////////////////
    const [mobileTabs, setMobilesTabs] = useState<string[]>([]);
    const tabsRef = useRef<RefObject<HTMLDivElement>[]>(
        Array.from({ length: 4 as number }, () => createRef())
    );

    const mainRef = useRef<HTMLDivElement>(null);
    const galleryRef = useRef<HTMLDivElement>(null);
    const infoRef = useRef<HTMLDivElement>(null);
    const similarCarsStaticRef = useRef<HTMLDivElement>(null);
    const getDimensions = (ele: HTMLDivElement) => {
        const { height } = ele.getBoundingClientRect();
        const offsetTop = ele.offsetTop;
        const offsetBottom = offsetTop + height;
        return {
            height,
            offsetTop,
            offsetBottom,
        };
    };
    const handleScroll = () => {
        if (!mainRef?.current || !galleryRef?.current || !infoRef?.current)
            return;
        const { height: galleryHeight } =
            galleryRef.current.getBoundingClientRect();
        const { height: infoHeight } = infoRef.current.getBoundingClientRect();
        const UPPER_MARGIN_HEIGHT = IS_DESKTOP
            ? galleryHeight
            : galleryHeight + infoHeight + 60;
        const scrollPosition = window.scrollY - UPPER_MARGIN_HEIGHT + 24;
        tabsRef?.current?.forEach((ref: { current: any }, index: number) => {
            const ele = ref.current;

            if (ele) {
                const { offsetBottom, offsetTop } = getDimensions(ele);

                if (
                    scrollPosition > offsetTop &&
                    scrollPosition < offsetBottom
                ) {
                    setValue(index);
                }
            }
        });
    };
    const handleInitialTabsLogic = () => {
        let tabs = [];
        tabs = [
            t('carGuarantee'),
            t('carSummary'),
            t('Overview'),
            t('inspection'),
            t('Features'),
            t('Financing'),
        ];
        if (!IS_DESKTOP) {
            tabs.splice(0, 2);
        } else {
            if (tabs[0] != t('carGuarantee')) {
                tabs.splice(0, 0, t('carGuarantee'));
            }
            if (tabs[1] != t('carSummary')) {
                tabs.splice(1, 0, t('carSummary'));
            }
        }

        setMobilesTabs(tabs);
        tabsRef.current = Array.from({ length: tabs.length as number }, () =>
            createRef()
        );
    };
    const getSectionRefIndex = (key: string) => {
        return mobileTabs.findIndex(item => item === key);
    };
    useEffect(() => {
        dispatch(get3dImperfections(carDetails.id));
        setCarDetailsState(carDetails);
        IntiateCarOverview(carDetails);
        IntiateCarInfo(carDetails);
        intiatCarFeatures(carDetails);
        const hasDiscount =
            carDetails?.discountPrice !== undefined &&
            carDetails?.discountPrice !== 0;
        const displayedPrice: number = hasDiscount
            ? carDetails?.discountPrice
            : parseFloat(carDetails?.sylndrSellingPrice as string);
        handleInitialTabsLogic();
        setCarPrice(displayedPrice);
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [carDetails, IS_DESKTOP]);

    useEffect(() => {
        window.addEventListener('scroll', listenScrollEvent);
        setTimeout(() => {
            window.scrollTo({ top: 1 });
        }, 0);
        setScrollToPosition(similarCarsStaticRef?.current?.offsetTop || 0);
        return () => {
            window.removeEventListener('scroll', listenScrollEvent);
        };
    }, []);

    const imprefections3d = useAppSelector(
        state => state.gallery.imperfections3d ?? []
    );

    const getIconFromStatus = (status: ImperfectionStatusEnum): ReactNode => {
        switch (status) {
            case ImperfectionStatusEnum.Faulty:
                return (
                    <Image
                        src={FaultyCheckMark}
                        width="20"
                        height="20"
                        alt="Faulty"
                    />
                );
            case ImperfectionStatusEnum.UnFaulty:
                return (
                    <Image
                        src={ImperfectionCheckMark}
                        width="20"
                        height="20"
                        alt="Good"
                    />
                );
            case ImperfectionStatusEnum.Fixed:
                return (
                    <Image src={FixedMark} width="20" height="20" alt="Fixed" />
                );
            default:
                return null;
        }
    };
    useEffect(() => {
        if (imperfectionDataGroupped) {
            intiateImperfectionsReportData(imperfectionDataGroupped);
        }
    }, [imperfectionDataGroupped]);
    const intiateImperfectionsReportData = (
        ImpReport: ImperfectionReportSectionGrouped
    ) => {
        const imperfectionsGalleryCategories: any[] = [];
        const imperfectionsGallery: any[] = [];
        const exterior = ImpReport?.exterior;
        const faulty = ImpReport?.faulty;

        exterior?.forEach((section: ImperfectionReportSection) => {
            const properties: IQCReportUIProperty[] = [];
            section.items.forEach((reportItem: ImperfectionReportItem) => {
                const propertyObj = {
                    id: reportItem.imperfection_item_id,
                    name: IS_RTL ? reportItem.name_ar : reportItem.name_en,
                    status: reportItem.status as ImperfectionStatusEnum,
                    value: getIconFromStatus(reportItem.status),
                    comment: IS_RTL
                        ? reportItem.comment_ar
                        : reportItem.comment_en,
                    tags: reportItem?.tags?.map(tag =>
                        IS_RTL ? tag.tag_ar : tag.tag_en
                    ),
                    images: reportItem.images,
                };
                if (reportItem.images && reportItem.images.length > 0) {
                    const categoryObj = {
                        name: IS_RTL ? section.name_ar : section.name_en,
                        icon: (
                            <Image
                                width={36}
                                height={36}
                                style={{ objectFit: 'contain' }}
                                src={section?.section_good_icon}
                                alt={section.name_en}
                            />
                        ),
                    };
                    const categoryIndex =
                        imperfectionsGalleryCategories.findIndex(
                            category => category.name === categoryObj.name
                        );
                    if (categoryIndex === -1) {
                        imperfectionsGalleryCategories.push(categoryObj);
                    }

                    const toBeAddedImages = reportItem.images.map(image => ({
                        newGallery: carDetails?.newGallery,
                        original: image,
                        thumbnail: image,
                        name: IS_RTL ? reportItem.name_ar : reportItem.name_en,
                        comment: IS_RTL
                            ? reportItem.comment_ar
                            : reportItem.comment_en,
                        tags: reportItem?.tags?.map(tag =>
                            IS_RTL ? tag.tag_ar : tag.tag_en
                        ),
                        category: imperfectionsGalleryCategories.findIndex(
                            (category: any) =>
                                category.name === categoryObj.name
                        ),
                        sectionImg: reportItem?.highlightedImages?.[0] ?? null,
                    }));
                    imperfectionsGallery.push(...toBeAddedImages);
                }
                properties.push(propertyObj);
            });
        });

        faulty?.forEach((section: ImperfectionReportSection) => {
            const properties: IQCReportUIProperty[] = [];
            section.items.forEach((reportItem: ImperfectionReportItem) => {
                const propertyObj = {
                    id: reportItem.imperfection_item_id,
                    name: IS_RTL ? reportItem.name_ar : reportItem.name_en,
                    status: reportItem.status as ImperfectionStatusEnum,
                    value: getIconFromStatus(reportItem.status),
                    comment: IS_RTL
                        ? reportItem.comment_ar
                        : reportItem.comment_en,
                    tags: reportItem?.tags?.map(tag =>
                        IS_RTL ? tag.tag_ar : tag.tag_en
                    ),
                    images: reportItem.images,
                };
                if (reportItem.images && reportItem.images.length > 0) {
                    const categoryObj = {
                        name: IS_RTL ? section.name_ar : section.name_en,
                        icon: (
                            <Image
                                width={36}
                                height={36}
                                style={{ objectFit: 'contain' }}
                                src={section?.section_good_icon}
                                alt={section.name_en}
                            />
                        ),
                    };
                    const categoryIndex =
                        imperfectionsGalleryCategories.findIndex(
                            category => category.name === categoryObj.name
                        );
                    if (categoryIndex === -1) {
                        imperfectionsGalleryCategories.push(categoryObj);
                    }

                    const toBeAddedImages = reportItem.images.map(image => ({
                        newGallery: carDetails?.newGallery,
                        original: image,
                        thumbnail: image,
                        name: IS_RTL ? reportItem.name_ar : reportItem.name_en,
                        comment: IS_RTL
                            ? reportItem.comment_ar
                            : reportItem.comment_en,
                        tags: reportItem?.tags?.map(tag =>
                            IS_RTL ? tag.tag_ar : tag.tag_en
                        ),
                        category: imperfectionsGalleryCategories.findIndex(
                            (category: any) =>
                                category.name === categoryObj.name
                        ),
                        sectionImg: reportItem?.highlightedImages?.[0] ?? null,
                    }));
                    imperfectionsGallery.push(...toBeAddedImages);
                }
                properties.push(propertyObj);
            });
        });
        dispatch(
            setImperfectionsImagesCategories(imperfectionsGalleryCategories)
        );
        dispatch(setImperfectionsImages(imperfectionsGallery));
    };

    const grouppedImperfectionItemClicked = (index: number) => {
        setInitialOpenedTab(index);
        setOpenGrouppedImprefectionModal(true);
    };
    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        if (!galleryRef?.current || !infoRef?.current) return;
        const { height: galleryHeight } =
            galleryRef.current.getBoundingClientRect();
        const { height: infoHeight } = infoRef.current.getBoundingClientRect();
        const { offsetTop } = getDimensions(tabsRef.current[newValue].current);
        const UPPER_MARGIN_HEIGHT = IS_DESKTOP
            ? galleryHeight
            : galleryHeight + infoHeight + 60;
        window.scrollTo({
            top: offsetTop + UPPER_MARGIN_HEIGHT + 24,
            left: 0,
            behavior: 'smooth',
        });
    };
    const carFeaturesObject = {
        'Performance Features': PerformanceFeatureIconImg,
        'Comfort & Convenience': ComfortConvenienceIconImg,
        'Entertainment & Communication': EntertainmentCommunicationIconImg,
        Interiors: InteriorIconImg,
        Safety: SafetyIconImg,
        Exteriors: ExteriorIconImg,
        'Dimensions & Capacity': DimensionsCapacityIconImg,
        'Sylndr certified': SylndrCertifiedIcon,
    };
    const featuresLabelsAndImages = {
        PerformanceFeatures: {
            name: t('Performance Features'),
            element: (
                <Image
                    src={PerformanceFeatureIconImg}
                    alt="PerformanceFeatureIconImg"
                    height={32}
                    width={32}
                />
            ),
            index: 0,
        },
        ComfortFeatures: {
            name: t('Comfort & Convenience'),
            element: (
                <Image
                    src={ComfortConvenienceIconImg}
                    alt="ComfortConvenienceIconImg"
                    height={32}
                    width={32}
                />
            ),
            index: 1,
        },
        EntertainmentFeatures: {
            name: t('Entertainment & Communication'),
            element: (
                <Image
                    src={EntertainmentCommunicationIconImg}
                    alt="EntertainmentCommunicationIconImg"
                    height={32}
                    width={32}
                />
            ),
            index: 2,
        },
        InteriorFeatures: {
            name: t('Interiors'),
            element: (
                <Image
                    src={InteriorIconImg}
                    alt="InteriorIconImg"
                    height={32}
                    width={32}
                />
            ),
            index: 3,
        },
        SafteyFeatures: {
            name: t('Safety'),
            element: (
                <Image
                    src={SafetyIconImg}
                    alt="SafetyIconImg"
                    height={32}
                    width={32}
                />
            ),
            index: 4,
        },

        ExteriorFeatures: {
            name: t('Exteriors'),
            element: (
                <Image
                    src={ExteriorIconImg}
                    alt="ExteriorIconImg"
                    height={32}
                    width={32}
                />
            ),
            index: 5,
        },
        DimensionFeatures: {
            name: t('Dimensions & Capacity'),
            element: (
                <Image
                    src={DimensionsCapacityIconImg}
                    alt="DimensionsCapacityIconImg"
                    height={32}
                    width={32}
                />
            ),
            index: 6,
        },
        SylndrAssured: {
            name: t('Sylndr certified'),
            element: (
                <Image
                    src={SylndrCertified}
                    alt="SylndrCertified"
                    height={32}
                    width={32}
                />
            ),
            index: 7,
        },
    };
    const IntiateCarOverview = (carDetails: any) => {
        setCarOverviewState(buildCarOverview(carDetails));
    };

    const IntiateCarInfo = (carDetails: any) => {
        setCarInfo(buildCarInfo(carDetails));
    };
    const intiatCarFeatures = (carDetails: any) => {
        const tempArr = Array(carDetails?.vehicleFeature.length);
        for (let j = 0; j < carDetails?.vehicleFeature.length; j += 1) {
            const properties = carDetails.vehicleFeature[j].featureProperties;
            const featureSection = carDetails.vehicleFeature[j]?.name;
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let k = 0; k < properties.length; k += 1) {
                Feature_SKELETON[
                    featureSection as keyof typeof Feature_SKELETON
                ].set(properties[k]?.name, properties[k]);
            }
        }
        for (let i = 0; i < carDetails?.vehicleFeature.length; i += 1) {
            if (carDetails?.vehicleFeature[i]) {
                const featureSection = carDetails?.vehicleFeature[i].name;
                const name =
                    featuresLabelsAndImages[
                        featureSection as keyof typeof featuresLabelsAndImages
                    ].name;

                const properties: CarFeatureInterface[] = [];
                Feature_SKELETON[
                    featureSection as keyof typeof Feature_SKELETON
                ]?.forEach(item => {
                    if (item.value !== '') properties.push(item);
                });

                const featureObj: IModalData = {
                    icon: featuresLabelsAndImages[
                        featureSection as keyof typeof featuresLabelsAndImages
                    ].element,
                    name: name,
                    data: {
                        properties: [...properties],
                        title: name,
                    },
                    applyGallery: carDetails?.newGallery,
                };

                tempArr[
                    featuresLabelsAndImages[
                        carDetails?.vehicleFeature[i]
                            .name as keyof typeof featuresLabelsAndImages
                    ].index
                ] = featureObj;
            }
        }

        if (tempArr.length > 0) {
            setCarFeaturesNew(tempArr);
        }
    };
    const featureitemClicked = (id: string, isViewAll = false) => {
        const featureKeys = Object.keys(carFeaturesObject);
        const target = featureKeys.indexOf(id);
        const index = target === featureKeys.length - 1 ? 0 : target;
        trackEvent('Action: Features', {
            sourceButton: isViewAll ? 'View All Features' : featureKeys[target],
            carName: carDetails?.sfVehicleName,
        });

        setFeatureValue(index);
        setOpenFeatureContainer(true);
    };
    const mobileButtons = () =>
        !IS_DESKTOP &&
        !(carDetails?.isSold || carDetails?.isPurchaseInProgress) && (
            <Box
                sx={{
                    position: 'sticky',
                    bottom: '0px',
                    background: 'white',
                    padding: '16px 12px',
                    boxShadow: '0px 24px 48px -12px rgba(16, 24, 40, 0.18)',
                    borderTop: '1px solid #EAECF0',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    height: '98px',
                }}
                className={styles.hideDesktop}
            >
                {carDetailsState?.isReserved && carInfo?.id ? (
                    <NotifyMeButton carId={carInfo?.id as string} />
                ) : carInfo?.bookingType &
                  (isReserveHubAllowed(carInfo?.bookingType) ||
                      isReserveHomeAllowed(carInfo?.bookingType)) ? (
                    <Link
                        disabled={NEXT_APP_MAINTENANCE_DISABLE_BOOKING}
                        href={`/${lang}/car-details/${carInfo?.id}/checkout/reserve`}
                        style={{
                            textDecoration: 'none',
                            width: '100%',
                            height: '80%',
                        }}
                    >
                        <SylndrButton
                            onClick={() => {
                                trackEvent('Action: Reserve Car', {
                                    carName: carDetails?.sfVehicleName,
                                    newlyAdded:
                                        carDetails.isNewlyAdded &&
                                        !(
                                            carDetails.isReserved ||
                                            carDetails.isPurchaseInProgress
                                        ),
                                    status: carDetails.isPurchaseInProgress
                                        ? 'Being Sold'
                                        : carDetails.isReserved
                                        ? 'Reserved'
                                        : 'Not Reserved',
                                });

                                dispatch(toggleLoading(false));
                            }}
                            style={{ height: '100%' }}
                            disabled={
                                carDetailsState?.isReserved ||
                                NEXT_APP_MAINTENANCE_DISABLE_BOOKING
                            }
                            variant={'primary'}
                        >
                            <Typography variant="body1SemiBold">
                                {t('reserve car')}
                            </Typography>
                        </SylndrButton>
                    </Link>
                ) : (
                    <></>
                )}

                {carInfo?.id &&
                carInfo?.bookingType &
                    (isSeeHomeAllowed(carInfo?.bookingType) ||
                        isSeeHubAllowed(carInfo?.bookingType)) ? (
                    <Link
                        disabled={NEXT_APP_MAINTENANCE_DISABLE_BOOKING}
                        href={`/${lang}/car-details/${carInfo.id}/checkout/see`}
                        style={{
                            textDecoration: 'none',
                            width: '100%',
                            height: '80%',
                        }}
                    >
                        <SylndrButton
                            onClick={() => {
                                trackEvent('Action: Book Visit', {
                                    carName: carDetails?.sfVehicleName,
                                    newlyAdded:
                                        carDetails.isNewlyAdded &&
                                        !(
                                            carDetails.isReserved ||
                                            carDetails.isPurchaseInProgress
                                        ),
                                    status: carDetails.isPurchaseInProgress
                                        ? 'Being Sold'
                                        : carDetails.isReserved
                                        ? 'Reserved'
                                        : 'Not Reserved',
                                });

                                dispatch(toggleLoading(false));
                            }}
                            style={{ height: '100%' }}
                            disabled={NEXT_APP_MAINTENANCE_DISABLE_BOOKING}
                            variant={
                                !(
                                    carInfo.bookingType &
                                    (isReserveHubAllowed(
                                        carInfo?.bookingType
                                    ) ||
                                        isReserveHomeAllowed(
                                            carInfo?.bookingType
                                        ))
                                ) || carInfo.isReserved
                                    ? 'primary'
                                    : 'secondary'
                            }
                        >
                            <Typography variant="body1SemiBold">
                                {t('See Car')}
                            </Typography>
                        </SylndrButton>
                    </Link>
                ) : (
                    <></>
                )}
            </Box>
        );

    const carCardLocalizations = getCarCardLocale();

    return (
        <div
            className={`${
                openGrouppedImprefectionModal && '!overflow-y-hidden '
            }`}
        >
            <CarProfileContext.Provider
                value={{
                    scrollToPosition,
                    setScrollToPosition,
                }}
            >
                <Box sx={pageContainer}>
                    <div className={styles.containerInner}>
                        {(carDetails?.isSold ||
                            carDetails?.isPurchaseInProgress) &&
                            !IS_DESKTOP && (
                                <CarSoldInfoBoxMobile
                                    type={
                                        carDetails?.isPurchaseInProgress
                                            ? 'PURCHASE_IN_PROGRESS'
                                            : 'SOLD'
                                    }
                                />
                            )}
                        {carDetails.isReserved &&
                            !carDetails.isPurchaseInProgress && (
                                <ReserveInfoBox />
                            )}

                        <div className={styles.layout}>
                            <Box
                                ref={galleryRef}
                                sx={{ ...gallery, gridArea: 'img' }}
                            >
                                {carDetailsState !== undefined ? (
                                    carDetailsState.newGallery &&
                                    carDetailsState.gallery.gallery ? (
                                        <Gallery
                                            buckets={
                                                carDetailsState.gallery.gallery
                                            }
                                            images={(
                                                carDetailsState?.frames ||
                                                carDetailsState?.images ||
                                                []
                                            ).map(imgUrl =>
                                                myImageLoader({
                                                    src: imgUrl,
                                                    width: framesWidth,
                                                    height: framesHeight,
                                                    quality: 95,
                                                })
                                            )}
                                            imperfections={imprefections3d}
                                            fireEvent={fireEvent}
                                            interior3d={
                                                carDetailsState?.interior3d
                                            }
                                        />
                                    ) : (
                                        <CarouselV2
                                            showDots={true}
                                            autoPlay={false}
                                            frameDuration={3000}
                                            showArrows={true}
                                        >
                                            {carDetailsState?.images.map(
                                                (img, i) => (
                                                    <div
                                                        className={'w-full'}
                                                        key={img}
                                                    >
                                                        <img
                                                            className={
                                                                'object-contain inline-block cursor-pointer box-border p-0 rounded-md w-full max-w-[95vw] h-auto lg:w-full lg:max-w-full'
                                                            }
                                                            fetchPriority={
                                                                i === 0
                                                                    ? 'high'
                                                                    : 'low'
                                                            }
                                                            loading={
                                                                i === 0
                                                                    ? 'eager'
                                                                    : 'lazy'
                                                            }
                                                            src={myImageLoader({
                                                                src: img,
                                                                width: 100,
                                                                height: 100,
                                                                quality: 70,
                                                            })}
                                                            alt={
                                                                ' sold vehicle'
                                                            }
                                                        />
                                                        ;
                                                    </div>
                                                )
                                            )}
                                        </CarouselV2>
                                    )
                                ) : (
                                    <BlinkingSkeleton
                                        height={IS_DESKTOP ? '50vw' : '40vw'}
                                        minHeight={IS_DESKTOP ? '45vw' : '50vh'}
                                    />
                                )}
                            </Box>

                            <div
                                style={{
                                    position: 'relative',
                                    gridArea: 'dt',
                                }}
                                ref={mainRef}
                            >
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        top: -1,
                                        backgroundColor: 'white',
                                        zIndex: 2,
                                        marginBottom: '24px',
                                    }}
                                >
                                    <StyledTabs
                                        // className={classes.root}
                                        value={value}
                                        onChange={handleChange}
                                        variant="scrollable"
                                        scrollButtons={false}
                                    >
                                        {mobileTabs?.map(item => {
                                            return (
                                                <StyledTab
                                                    label={
                                                        <ModalTabsText variant="body2Medium">
                                                            {item}
                                                        </ModalTabsText>
                                                    }
                                                    key={item}
                                                />
                                            );
                                        })}
                                    </StyledTabs>
                                </Box>

                                <div
                                    ref={
                                        tabsRef?.current[
                                            getSectionRefIndex(
                                                t('carGuarantee')
                                            )
                                        ]
                                    }
                                    className="hidden md:block w-full  mb-[2vw]"
                                >
                                    <CarGuarantee />
                                </div>
                                <div
                                    ref={
                                        tabsRef?.current[
                                            getSectionRefIndex(t('carSummary'))
                                        ]
                                    }
                                    className="hidden md:block w-full  mb-[2vw]"
                                >
                                    <div className="text-[#002d5d] text-xl font-bold leading-[30px] mb-2">
                                        {t('carSummary')}
                                    </div>
                                    <div className="border flex flex-wrap border-solid border-gray-100 rounded-lg py-6 px-4 gap-5">
                                        <div className="flex  gap-x-2 p-x-1 border-r border-solid border-gray-100 w-[calc(33.333%-1.25rem)] items-center last:border-0">
                                            <Image
                                                className="w-5 h-5"
                                                src={carFrame}
                                                alt="frame"
                                            />
                                            {imperfectionDataGroupped?.isRepainted ? (
                                                imperfectionDataGroupped?.numberOfRepaintedPanels ==
                                                1 ? (
                                                    <div className="text-gray-600 text-sm font-normal leading-tight ar:mt-1">
                                                        {
                                                            imperfectionDataGroupped?.numberOfRepaintedPanels
                                                        }{' '}
                                                        {t('repaintedPart')}
                                                    </div>
                                                ) : imperfectionDataGroupped?.numberOfRepaintedPanels >
                                                  3 ? (
                                                    <div className="text-gray-600 text-sm font-normal leading-tight">
                                                        {t('fewRepaintedParts')}
                                                    </div>
                                                ) : (
                                                    <div className="text-gray-600 text-sm font-normal leading-tight ar:mt-1">
                                                        {
                                                            imperfectionDataGroupped?.numberOfRepaintedPanels
                                                        }{' '}
                                                        {t('repaintedParts')}
                                                    </div>
                                                )
                                            ) : (
                                                <div className="text-gray-600 text-sm font-normal leading-tight ar:mt-1">
                                                    {t('noRepaintedParts')}
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex  gap-x-2 p-x-1 border-r border-solid border-gray-100 w-[calc(33.333%-1.25rem)] items-center last:border-0 ">
                                            <Image
                                                className="w-5 h-5"
                                                src={engine}
                                                alt="engine"
                                            />

                                            <div className="text-gray-600 text-sm font-normal leading-tight ar:mt-1">
                                                {t('noMechProblems')}
                                            </div>
                                        </div>

                                        {carDetailsState?.highlights?.ar.map(
                                            (h, index) => (
                                                <div
                                                    className={`flex  gap-x-2 px-1 ${
                                                        index % 3 == 0
                                                            ? ''
                                                            : 'border-r border-solid border-gray-100'
                                                    } w-[calc(33.333%-1.25rem)] items-center last:border-0 `}
                                                    key={`${h} highlight-item`}
                                                >
                                                    <Image
                                                        className="w-5 h-5"
                                                        src={HighlightsIcon}
                                                        alt="star"
                                                    />
                                                    <div className="en:hidden text-gray-600 text-sm font-normal leading-tight mt-1">
                                                        {
                                                            carDetailsState
                                                                .highlights?.ar[
                                                                index
                                                            ]
                                                        }
                                                    </div>
                                                    <div className="ar:hidden text-gray-600 text-sm font-normal leading-tight">
                                                        {
                                                            carDetailsState
                                                                .highlights?.en[
                                                                index
                                                            ]
                                                        }
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                                <Box
                                    ref={
                                        tabsRef?.current[
                                            getSectionRefIndex(t('Overview'))
                                        ]
                                    }
                                    sx={{ ...carOverview, gridArea: 'ov' }}
                                >
                                    <GridSection
                                        gridItemVariant={
                                            EGGRID_ITEM_VARIANT.TEXT
                                        }
                                        sectionHeader={t('Car Overview')}
                                        gridItems={
                                            carOverviewState ??
                                            buildCarOverview(carDetails)
                                        }
                                    />
                                </Box>
                                <Box
                                    sx={{ gridArea: 'df', marginBottom: '2vw' }}
                                >
                                    {!IS_DESKTOP && (
                                        <>
                                            {/*<ReserveSeeCarBanner variant="banner" />{' '}*/}
                                            <PurchaseCarSteps />
                                        </>
                                    )}
                                </Box>

                                {/* groupped imperfection report */}
                                <Box
                                    ref={
                                        tabsRef?.current[
                                            getSectionRefIndex(t('inspection'))
                                        ]
                                    }
                                    sx={{
                                        ...carInspection,
                                        gridArea: 'rp',
                                        paddingY: IS_DESKTOP ? '0' : '20px',
                                    }}
                                >
                                    {imperfectionDataGroupped && (
                                        <ImperfectionOverviewSectioned
                                            t={t}
                                            sectionHeader={t(
                                                'imperfectionReport'
                                            )}
                                            itemClicked={
                                                grouppedImperfectionItemClicked
                                            }
                                            imperfectionReportUnfaultyItems={
                                                imperfectionDataGroupped.unFaulty
                                            }
                                            lang={lang}
                                            isRepainted={
                                                imperfectionDataGroupped.isRepainted
                                            }
                                        />
                                    )}

                                    {carDetails &&
                                        imperfectionDataGroupped &&
                                        (IS_DESKTOP ? (
                                            <GrouppedImprefectionReportModal
                                                open={
                                                    openGrouppedImprefectionModal
                                                }
                                                setOpen={
                                                    closeGrouppedImprefectionModalHandler
                                                }
                                                carDisplayName={`${t(
                                                    carDetails?.make
                                                )} ${carDetails?.model} ${
                                                    carDetails.year
                                                }`}
                                                carImage={carDetails?.images[0]}
                                                imperfectionDataGroupped={
                                                    imperfectionDataGroupped
                                                }
                                                initialActiveTab={
                                                    initialOpenedTab
                                                }
                                                highlights={
                                                    carDetailsState?.highlights
                                                }
                                                IS_RTL={IS_RTL}
                                                t={t}
                                            />
                                        ) : (
                                            <GrouppedImprefectionReportBottomSheet
                                                carInfo={carInfo}
                                                carDetails={carDetails}
                                                lang={lang}
                                                open={
                                                    openGrouppedImprefectionModal
                                                }
                                                setOpen={
                                                    closeGrouppedImprefectionModalHandler
                                                }
                                                carDisplayName={`${t(
                                                    carDetails?.make
                                                )} ${carDetails?.model} ${
                                                    carDetails.year
                                                }`}
                                                carImage={carDetails?.images[0]}
                                                imperfectionDataGroupped={
                                                    imperfectionDataGroupped
                                                }
                                                initialActiveTab={
                                                    initialOpenedTab
                                                }
                                                highlights={
                                                    carDetailsState?.highlights
                                                }
                                                IS_RTL={IS_RTL}
                                                t={t}
                                            />
                                        ))}
                                </Box>

                                <Box
                                    ref={
                                        tabsRef?.current[
                                            getSectionRefIndex(t('Features'))
                                        ]
                                    }
                                    sx={{ ...carFeature, gridArea: 'ft' }}
                                >
                                    <GridSection
                                        gridItemVariant={
                                            EGGRID_ITEM_VARIANT.TEXT_WITH_ICON
                                        }
                                        sectionHeader={t('Car Features')}
                                        gridItems={carFeaturesObject}
                                        viewAll={t('View All Features') || ''}
                                        gridItemClickable={true}
                                        gridItemClicked={featureitemClicked}
                                    ></GridSection>
                                    {carFeaturesNew &&
                                        (!IS_DESKTOP ? (
                                            <SwipeableDrawer
                                                open={openFeatureContainer}
                                                setOpen={
                                                    setOpenFeatureContainer
                                                }
                                            >
                                                <ModalInfo
                                                    isSold={
                                                        carDetails?.isSold ||
                                                        carDetails?.isPurchaseInProgress
                                                    }
                                                    id={carDetailsState?.id}
                                                    isReserved={
                                                        carDetailsState?.isReserved
                                                    }
                                                    valueType="text"
                                                    data={carFeaturesNew}
                                                    title={t('Car Features')}
                                                    clickedItem={featureValue}
                                                    open={openFeatureContainer}
                                                    setOpen={
                                                        setOpenFeatureContainer
                                                    }
                                                    bookingType={
                                                        carDetails?.bookingType
                                                    }
                                                />
                                            </SwipeableDrawer>
                                        ) : (
                                            <Modal
                                                open={openFeatureContainer}
                                                setOpen={
                                                    setOpenFeatureContainer
                                                }
                                            >
                                                <Box
                                                    sx={{
                                                        width: '646px',
                                                        padding: '24px',
                                                    }}
                                                >
                                                    <ModalInfo
                                                        isSold={
                                                            carDetails?.isSold ||
                                                            carDetails?.isPurchaseInProgress
                                                        }
                                                        id={carDetailsState?.id}
                                                        isReserved={
                                                            carDetailsState?.isReserved
                                                        }
                                                        valueType="text"
                                                        data={carFeaturesNew}
                                                        title={t(
                                                            'Car Features'
                                                        )}
                                                        clickedItem={
                                                            featureValue
                                                        }
                                                        open={
                                                            openFeatureContainer
                                                        }
                                                        setOpen={
                                                            setOpenFeatureContainer
                                                        }
                                                        bookingType={
                                                            carDetails?.bookingType
                                                        }
                                                    />
                                                </Box>
                                            </Modal>
                                        ))}
                                </Box>

                                <Box sx={whySylndrContainer}>
                                    <ValuePropositionSlider
                                        bannerWidth={183}
                                        bannerHeight={247}
                                    />
                                </Box>

                                <Box
                                    ref={
                                        tabsRef?.current[
                                            getSectionRefIndex(t('Financing'))
                                        ]
                                    }
                                    sx={{ gridArea: 'cl' }}
                                >
                                    <Typography
                                        style={sectionHeader}
                                        variant={'subtitle1Bold'}
                                    >
                                        {t('Financing')}
                                    </Typography>
                                    {carInfo && carPrice && (
                                        <FinanceCalculator
                                            carPrice={carPrice}
                                            lenders={carDetailsState?.lenders}
                                            eventName={
                                                'Action: Profile Financing'
                                            }
                                            carName={carDetails?.sfVehicleName}
                                            isSold={carDetails?.isSold}
                                            isPurchaseInProgress={
                                                carDetails?.isPurchaseInProgress
                                            }
                                            carInfo={carInfo}
                                            mobileButtons={
                                                mobileButtons() as JSX.Element
                                            }
                                        />
                                    )}
                                </Box>
                            </div>
                            <Box
                                ref={infoRef}
                                sx={{ ...stickyContainer, gridArea: 'mn' }}
                            >
                                {
                                    <CarInfo
                                        IS_DESKTOP={IS_DESKTOP}
                                        carDetails={carDetails}
                                        IS_RTL={IS_RTL}
                                        inspectionRef={
                                            tabsRef?.current[
                                                getSectionRefIndex(
                                                    t('inspection')
                                                )
                                            ]
                                        }
                                        highlights={carDetailsState?.highlights}
                                        isRepainted={
                                            imperfectionDataGroupped?.isRepainted
                                        }
                                        numberOfRepaintedPanels={
                                            imperfectionDataGroupped?.numberOfRepaintedPanels
                                        }
                                        carInfo={
                                            carInfo ?? buildCarInfo(carDetails)
                                        }
                                    />
                                }
                                {IS_DESKTOP && (
                                    <Box marginTop={'24px'}>
                                        {/*<ReserveSeeCarBanner variant="banner" />{' '}*/}
                                        <PurchaseCarSteps />
                                    </Box>
                                )}
                                <div
                                    className={'block md:hidden direction-ltr'}
                                >
                                    <div
                                        style={{
                                            height: '110px',
                                        }}
                                    >
                                        <Carousel
                                            showDots={false}
                                            containerWidth={'250px'}
                                            containerHeight={'94px'}
                                            imageMargin="8px"
                                            imgs={IS_RTL ? bannersAR : banners}
                                            slideShow={false}
                                            dragable={true}
                                            lang={lang}
                                        />
                                    </div>
                                </div>
                            </Box>
                        </div>
                    </div>
                    {(carDetails?.isSold || carDetails?.isPurchaseInProgress) &&
                        similarCars && (
                            <SimilarCarsContainer
                                similarCars={similarCars}
                                opacity={opacity}
                            />
                        )}
                    <Box sx={similarCarsContainer} ref={similarCarsStaticRef}>
                        <Typography
                            style={similarCarsText}
                            variant={'subtitle2Medium'}
                        >
                            {t('Similar Cars')}
                        </Typography>

                        <div
                            className={`${styles.similarCarsContainer} w-full`}
                        >
                            {similarCars?.data
                                ?.slice(0, 8)
                                .map((item: CarListingData, index: number) => (
                                    <CarDisplayCardClient
                                        carCardLocalizations={
                                            carCardLocalizations
                                        }
                                        lang={lang}
                                        febDiscount={febDiscounts[item.id] || 0}
                                        id={item.id}
                                        key={item.id}
                                        cardTitle={`${t(item.make)} ${
                                            item.model
                                        } ${item.year}`}
                                        carName={item.sfVehicleName}
                                        trim={item.trim}
                                        installmentPrice={
                                            item?.installmentPrice
                                        }
                                        totalPrice={item.sylndrSellingPrice}
                                        kilometers={item.kilometers}
                                        year={item.year}
                                        transmission={item.transmission}
                                        images={
                                            item?.images.length !== 0
                                                ? item?.images
                                                : imgs
                                        }
                                        isReserved={item.isReserved}
                                        make={item.make}
                                        model={item.model}
                                        sourceScreen={'Car Profile'}
                                        appliedFilter={false}
                                        appliedSort={false}
                                        position={index + 1}
                                        isPurchaseInProgress={
                                            item.isPurchaseInProgress
                                        }
                                        discountPrice={item?.discountPrice}
                                        isNewlyAdded={item?.isNewlyAdded}
                                    />
                                ))}
                        </div>
                    </Box>
                    <div className={styles.testamonialContainer}>
                        <VidTestimonialsSection
                            SourceScreen={SourceScreen.CAR_PROFILE}
                            serviceName={ServiceName.RETAIL}
                        />
                    </div>
                </Box>
                {mobileButtons()}
            </CarProfileContext.Provider>
        </div>
    );
}
